import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { Indicator } from 'src/app/core/_models/indicator';
import { Scenario } from 'src/app/core/_models/scenario';
import { Solution } from 'src/app/core/_models/solution';
import { ProjectService } from 'src/app/core/_services/project.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { DialogOverviewComponent } from '../../../shared/dialog-overview/dialog-overview.component';
import { DialogScenarioComponent } from '../../../shared/dialog-scenario/dialog-scenario.component';
import { DialogSelectSolutionComponent } from '../../../shared/dialog-select-solution/dialog-select-solution.component';
import { ProjectDataService } from '../../project-data.service';

@Component({
  selector: 'app-project-step-result',
  templateUrl: './project-step-result.component.html',
  styleUrls: ['./project-step-result.component.scss']
})
export class ProjectStepResultComponent implements OnInit {
  @Input() projectForm!: FormGroup;
  @Input() projectId: string | null = null;

  @Output() onPreviousStep = new EventEmitter<void>();

  energies$: Observable<any[]>;
  indicators$: Observable<any[]>;
  isLoading$: Observable<boolean>;

  scenario: Scenario | null = null;
  indicators: Indicator[] = [];
  refreshKey: number = 0;

  hasModifications: boolean = false;

  constructor(
    private projectDataService: ProjectDataService,
    private projectService: ProjectService,
    private matDialog: MatDialog,
    private spinnerService: SpinnerOverlayService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService
  ) {
    this.energies$ = this.projectDataService.getEnergies();
    this.indicators$ = this.projectDataService.getIndicators();
    this.isLoading$ = this.projectDataService.loading$;
  }

  ngOnInit(): void {
    this.projectDataService.ensureDataLoaded().subscribe();
    this.loadIndicators().then(() => {
      this.loadScenarios();
    });
  }

  private loadScenarios(): void {
    if (this.projectId) {
      this.projectService.getScenarioByProjectId(this.projectId).subscribe((scenarios) => {
        this.scenario = scenarios.length > 0 ? scenarios[0] : null;
        if (this.scenario && this.indicators.length > 0) {
          if (!this.scenario.indicatorSelected) {
            this.scenario.indicatorSelected = [];
          }
          const indicatorIds = this.scenario.indicatorSelected.map((ind) => ind.indicatorId);
          const indicatorsToAdd = this.indicators.filter((ind) => !indicatorIds.includes(ind.indicatorId));
          if (indicatorsToAdd.length > 0) {
            this.scenario.indicatorSelected = [...this.scenario.indicatorSelected, ...indicatorsToAdd];
          }
        }
      });
    }
  }

  private loadIndicators(): Promise<void> {
    return this.projectService
      .getIndicators()
      .toPromise()
      .then((indicators: Indicator[]) => {
        this.indicators = indicators || [];
        this.checkIndicators();
      });
  }

  handlePreviousStep() {
    this.onPreviousStep.emit();
  }

  editScenario(item: Scenario) {
    const dialogRef = this.matDialog.open(DialogScenarioComponent, {
      data: {
        indicators: item.indicatorSelected,
        scenario: item
      }
    });

    dialogRef.afterClosed().subscribe(async (data) => {
      if (data?.scenario) {
        try {
          let payload = JSON.parse(JSON.stringify(data.scenario));
          payload.indicatorSelected = data.indicators?.filter((item: Indicator) => item.isChecked == true);
          let resp = await this.projectService.updateScenario(payload);
          item = Object.assign(item, resp);
        } catch (error) {
          this.snackbarService.open(SnackbarTypes.error);
        }
      }
    });
  }

  trackByIndexFn(index: number, item: any): number {
    return index;
  }

  async onSolutionDrop(event: CdkDragDrop<Solution[]>, scenario: Scenario) {
    if (event.previousIndex !== event.currentIndex) {
      this.spinnerService.show();

      const solutions = scenario.solutions;
      if (solutions) {
        moveItemInArray(solutions, event.previousIndex, event.currentIndex);

        const reorderedSolutions = solutions.map((solution, index) => ({
          ...solution,
          rank: index + 1
        }));

        try {
          const res = await this.projectService.editRank(scenario.scenarioId, reorderedSolutions);
          if (res) {
            this.spinnerService.hide();
            this.onSolutionModified();
          } else {
            this.spinnerService.hide();
            this.snackbarService.open(SnackbarTypes.error);
          }
        } catch (error) {
          this.spinnerService.hide();
          this.snackbarService.open(SnackbarTypes.error);
        }
      }
    }
  }

  addSolution(item: Scenario) {
    if (!this.projectId) return;

    this.projectService.getProjectsListForScenario(this.projectId).subscribe((projects) => {
      const dialogRef = this.matDialog.open(DialogSelectSolutionComponent, {
        width: '1100px',
        maxWidth: '95vw',
        data: {
          projects: projects
        }
      });

      dialogRef.afterClosed().subscribe(async (selectedSolution: Solution) => {
        if (selectedSolution) {
          try {
            if (!item?.solutions) {
              item.solutions = [];
            }
            let rank = item.solutions?.length ? item.solutions?.length : 0;
            let response: Solution = await this.projectService.addSolutionScenario(item.scenarioId, selectedSolution, rank);

            const solutionToAdd = {
              ...selectedSolution,
              id: response.id,
              rank: rank,
              scenarioSolutionAssociationId: response.scenarioSolutionAssociationId
            };

            item.solutions?.splice(rank, 0, solutionToAdd);
            this.onSolutionModified();
          } catch (error) {
            this.snackbarService.open(SnackbarTypes.error);
          }
        }
      });
    });
  }

  deleteSolution(i: number, scenario?: Scenario, currentSolution?: Solution) {
    let title = this.translateService.instant('project_dialog_confirm_deselect_solution');

    const dialogRef = this.matDialog.open(DialogOverviewComponent, {
      data: { title: title }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result && scenario?.solutions) {
        try {
          let solutions = scenario?.solutions;
          await this.projectService.deleteSolutionScenario(scenario?.scenarioId, currentSolution?.id, currentSolution?.rank);
          solutions.splice(i, 1);
          this.onSolutionModified();
        } catch (error) {
          this.snackbarService.open(SnackbarTypes.error);
        }
      }
    });
  }

  checkIndicators() {
    if (this.indicators) {
      this.indicators = this.indicators?.map((item: Indicator) => {
        if ((item.code == 'PE' || item.code == 'PB') && !this.projectForm.value?.useWaterAndBiodeversityIndicators) {
          return { ...item, isChecked: false };
        }
        return { ...item, isChecked: item.isChecked !== undefined ? item.isChecked : true };
      });
    }
  }

  toggleSynthese() {
    this.refreshSynthese();
  }

  refreshSynthese() {
    this.hasModifications = false;
    this.refreshKey++;
    this.loadScenarios();
  }

  onSolutionModified() {
    this.hasModifications = true;
  }

  onIndicatorChange(indicator: Indicator, event: any) {
    indicator.isChecked = event.checked;
    this.hasModifications = true;
    this.saveScenarioIndicators();
  }

  onIndicatorModified() {
    this.hasModifications = true;
  }

  private async saveScenarioIndicators() {
    if (this.scenario) {
      try {
        const payload = JSON.parse(JSON.stringify(this.scenario));
        payload.indicatorSelected = this.scenario.indicatorSelected?.filter((item: Indicator) => item.isChecked == true);
        await this.projectService.updateScenario(payload);
      } catch (error) {
        this.snackbarService.open(SnackbarTypes.error);
      }
    }
  }
}
